import { ErrorHandler, Injectable } from '@angular/core';

import {
  AprimaAnalyticsService,
  ConfigurationRepository,
} from 'webcommon/legacy-common';
import { ServerUtilRepository } from 'webcommon/webapi';

// This service handles all unhandled errors in the application,
// and provides an API for sending error reports to the server.
@Injectable({
  providedIn: 'root',
})
export class ApErrorHandler extends ErrorHandler {
  static readonly ajsFactoryName = 'ApErrorHandler';

  constructor(
    private readonly aprimaAnalyticsService: AprimaAnalyticsService,
    private readonly configurationRepository: ConfigurationRepository,
    private readonly serverUtilRepository: ServerUtilRepository,
  ) {
    super();
  }

  // This is the unhandled exception handler.
  // This should never be called directly,
  // only by Angular or the ajs AprimaExceptionHandler.
  override handleError(error: any) {
    try {
      super.handleError(error);

      if (isError(error)) {
        this.reportBackgroundError(error);
      }
    } catch (e: any) {
      this.handleErrorFromHandlingError(e);
    }
  }

  // This method reports the error to the server without showing any UI.
  // This probably should never be called directly because if the error is being caught,
  // it's probably important enough to be showing the error to the user.
  // If the error needs to be caught without showing UI,
  // the correct thing to do is to rethrow the error so that it gets caught by the unhandled exception handler above.
  reportBackgroundError(error: Error) {
    try {
      this.reportError(error, false);
    } catch (e: any) {
      this.handleErrorFromHandlingError(e);
    }
  }

  // This method reports the error to the server without showing any UI,
  // but reports the error to the server as being visible to the user so the caller must have shown its own error message first.
  // This should be called by code that was able to show an error message with more context to the user than the standard one.
  reportUserVisibleError(error: Error) {
    try {
      this.reportError(error, true);
    } catch (e: any) {
      this.handleErrorFromHandlingError(e);
    }
  }

  private reportError(error: Error, withUI: boolean) {
    try {
      this.aprimaAnalyticsService.sendException(error.message);
    } catch (sendExc: any) {
      console.error(sendExc);
    }

    if (this.configurationRepository.DISABLE_ERROR_REPORTING) {
      return;
    }

    this.serverUtilRepository.reportError({
      Message: error.message,
      StackTrace: error.stack,
      WithUI: withUI,
    }).subscribe(dto => {
      // tslint:disable-next-line:no-console
      console.debug('Reported error', dto);
    }, e => {
      this.handleErrorFromHandlingError(e);
    });
  }

  private handleErrorFromHandlingError(error: any) {
    console.error(error);
  }
}

function isError(error: any): error is Error {
  return error && 'message' in error && 'name' in error;
}
